/**
 * RebatesTab Component
 *
 * Unified interface for managing all types of rebates and incentives.
 * Supports federal, state, utility, manufacturer, and company promotions.
 *
 * Features:
 * - Single section for all rebates (promotions shown first)
 * - Add/edit/delete rebates with comprehensive modal
 * - Expiration tracking with urgency indicators
 * - Stackability rules configuration
 * - Filter by category and status
 * - Search functionality
 * - Smart sorting (promotions first, then by priority & expiration)
 */

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { logInfo, logError } from '../../utils/logger';

// Helper function to format camelCase system types to readable text
function formatSystemType(systemType) {
  if (!systemType) return '';
  // Add space before capital letters and trim
  return systemType.split('').map((char, index) => {
    if (index > 0 && char === char.toUpperCase() && char !== char.toLowerCase()) {
      return ' ' + char;
    }
    return char;
  }).join('').trim();
}

// Helper function to remove emoji prefix from category labels
function removeCategoryIcon(label) {
  if (!label) return '';
  // Find the first space and return everything after it
  const firstSpace = label.indexOf(' ');
  return firstSpace > 0 ? label.substring(firstSpace + 1) : label;
}

export default function RebatesTab() {
  const { contractor } = useAuth();
  const [rebates, setRebates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Filter/search state
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('active');
  const [searchQuery, setSearchQuery] = useState('');

  // Utility providers
  const [utilityProviders, setUtilityProviders] = useState([]);

  // Geographic jurisdictions
  const [jurisdictions, setJurisdictions] = useState([]);

  // System options
  const [systemOptions, setSystemOptions] = useState([]);

  // Info modal state
  const [showUtilityProviderInfo, setShowUtilityProviderInfo] = useState(false);
  const [showJurisdictionInfo, setShowJurisdictionInfo] = useState(false);
  const [showSystemOptionsModal, setShowSystemOptionsModal] = useState(false);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
  const [editingRebate, setEditingRebate] = useState(null);

  // Form state
  const [name, setName] = useState('');
  const [category, setCategory] = useState('contractor_promotion');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [amountType, setAmountType] = useState('fixed');
  const [jurisdictionIds, setJurisdictionIds] = useState([]);
  const [utilityProvider, setUtilityProvider] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [status, setStatus] = useState('active');
  const [displayPriority, setDisplayPriority] = useState(2);
  const [showExpirationCountdown, setShowExpirationCountdown] = useState(true);
  const [applicationNotes, setApplicationNotes] = useState('');
  const [requiredDocs, setRequiredDocs] = useState('');
  const [contactInfo, setContactInfo] = useState('');

  // Eligibility rules
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [minSEER, setMinSEER] = useState('');
  const [minHSPF, setMinHSPF] = useState('');
  const [minAFUE, setMinAFUE] = useState('');

  // Stackability rules
  const [isStackable, setIsStackable] = useState(true);
  const [allowedWith, setAllowedWith] = useState([]);
  const [notAllowedWith, setNotAllowedWith] = useState([]);

  // System type and option filters
  const [applicableSystemTypes, setApplicableSystemTypes] = useState([]);
  const [applicableSystemOptions, setApplicableSystemOptions] = useState([]);

  const categories = [
    { value: 'federal', label: '🏛️ Federal', icon: '🏛️' },
    { value: 'state', label: '🏛️ State', icon: '🏛️' },
    { value: 'county', label: '🏛️ County/Regional', icon: '🏛️' },
    { value: 'city', label: '🏛️ City/Township', icon: '🏛️' },
    { value: 'utility', label: '⚡ Utility Provider', icon: '⚡' },
    { value: 'manufacturer', label: '🏭 Manufacturer', icon: '🏭' },
    { value: 'contractor_promotion', label: '🎁 Company Promotion', icon: '🎁' },
    { value: 'other', label: '📋 Other', icon: '📋' }
  ];

  const systemTypes = [
    { id: 'centralAC', label: 'Central AC (Electric Cooling Only)', icon: '❄️' },
    { id: 'heatPump', label: 'Heat Pump (Electric Heating + Cooling)', icon: '🔥❄️' },
    { id: 'heatPumpPackage', label: 'Heat Pump Package Unit', icon: '📦🔥❄️' },
    { id: 'furnaceAC', label: 'Gas Furnace + AC (Dual Fuel)', icon: '🔥❄️' },
    { id: 'furnaceACPackage', label: 'Gas Furnace + AC Package Unit', icon: '📦🔥❄️' },
    { id: 'furnaceOnly', label: 'Gas Furnace Only', icon: '🔥' },
    { id: 'boiler', label: 'Boiler (Hydronic Heating)', icon: '♨️' },
    { id: 'ductlessMini', label: 'Ductless Mini-Split', icon: '🌡️' }
  ];

  // Load rebates
  useEffect(() => {
    if (contractor?.id) {
      loadRebates();
      loadUtilityProviders();
      loadJurisdictions();
      loadSystemOptions();
    }
  }, [contractor?.id]);

  async function loadRebates() {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('rebates')
        .select('*')
        .eq('contractor_id', contractor.id)
        .order('display_priority', { ascending: true })
        .order('end_date', { ascending: true })
        .order('name', { ascending: true });

      if (error) throw error;

      setRebates(data || []);
      logInfo('load_rebates', `Loaded ${data?.length || 0} rebates`, {
        count: data?.length || 0
      });
    } catch (err) {
      console.error('Error loading rebates:', err);
      logError('load_rebates', err, { contractor_id: contractor.id });
      setError('Failed to load rebates');
    } finally {
      setLoading(false);
    }
  }

  async function loadUtilityProviders() {
    try {
      const { data, error } = await supabase
        .from('utility_providers')
        .select('*')
        .eq('contractor_id', contractor.id)
        .order('provider_name');

      if (error) throw error;

      setUtilityProviders(data || []);
    } catch (err) {
      console.error('Error loading utility providers:', err);
    }
  }

  async function loadJurisdictions() {
    try {
      const { data, error } = await supabase
        .from('geographic_jurisdictions')
        .select('*')
        .eq('contractor_id', contractor.id)
        .order('jurisdiction_type', { ascending: true })
        .order('jurisdiction_name', { ascending: true });

      if (error) throw error;

      setJurisdictions(data || []);
    } catch (err) {
      console.error('Error loading jurisdictions:', err);
    }
  }

  async function loadSystemOptions() {
    try {
      const { data, error } = await supabase
        .from('system_options')
        .select('*')
        .eq('contractor_id', contractor.id)
        .eq('is_enabled', true)
        .order('system_type', { ascending: true })
        .order('option_number', { ascending: true });

      if (error) throw error;

      setSystemOptions(data || []);
    } catch (err) {
      console.error('Error loading system options:', err);
    }
  }

  // Calculate urgency and days remaining
  function getUrgencyInfo(endDate) {
    const end = new Date(endDate);
    const today = new Date();
    const daysRemaining = Math.ceil((end - today) / (1000 * 60 * 60 * 24));

    let urgency, color, label;
    if (daysRemaining < 0) {
      urgency = 'expired';
      color = '#94a3b8';
      label = 'Expired';
    } else if (daysRemaining <= 7) {
      urgency = 'urgent';
      color = '#dc2626';
      label = `URGENT: ${daysRemaining} days left!`;
    } else if (daysRemaining <= 14) {
      urgency = 'act_fast';
      color = '#ea580c';
      label = `${daysRemaining} days left - Act fast!`;
    } else if (daysRemaining <= 30) {
      urgency = 'expires_soon';
      color = '#f59e0b';
      label = `Expires in ${daysRemaining} days`;
    } else if (daysRemaining <= 90) {
      urgency = 'act_soon';
      color = '#84cc16';
      label = `${daysRemaining} days remaining`;
    } else {
      urgency = 'available';
      color = '#22c55e';
      label = `Valid for ${daysRemaining} more days`;
    }

    return { urgency, color, label, daysRemaining };
  }

  // Open add modal
  function handleAdd() {
    setModalMode('add');
    resetForm();
    setShowModal(true);
    setError(null);
    logInfo('open_rebate_modal', 'Opened rebate modal to add new rebate', { mode: 'add' });
  }

  // Open edit modal
  function handleEdit(rebate) {
    setModalMode('edit');
    setName(rebate.name);
    setCategory(rebate.category);
    setDescription(rebate.description || '');
    setAmount(rebate.amount.toString());
    setAmountType(rebate.amount_type);
    setJurisdictionIds(rebate.jurisdiction_ids || []);
    setUtilityProvider(rebate.utility_provider || '');
    setStartDate(rebate.start_date);
    setEndDate(rebate.end_date);
    setStatus(rebate.status);
    setDisplayPriority(rebate.display_priority);
    setShowExpirationCountdown(rebate.show_expiration_countdown);
    setApplicationNotes(rebate.application_notes || '');
    setRequiredDocs(rebate.required_docs || '');
    setContactInfo(rebate.contact_info || '');

    // Parse eligibility rules
    const rules = rebate.eligibility_rules || {};
    setEquipmentTypes(rules.equipment_types || []);
    setMinSEER(rules.min_seer || '');
    setMinHSPF(rules.min_hspf || '');
    setMinAFUE(rules.min_afue || '');

    // Parse stackability rules
    const stackRules = rebate.stackability_rules || {};
    setIsStackable(stackRules.stackable !== false);
    setAllowedWith(stackRules.allowed_with || []);
    setNotAllowedWith(stackRules.not_allowed_with || []);

    // Parse system filters
    setApplicableSystemTypes(rebate.applicable_system_types || []);
    setApplicableSystemOptions(rebate.applicable_system_options || []);

    setEditingRebate(rebate);
    setShowModal(true);
    setError(null);
    logInfo('open_rebate_modal', 'Opened rebate modal to edit rebate', {
      mode: 'edit',
      rebate_id: rebate.id,
      rebate_name: rebate.name,
      category: rebate.category
    });
  }

  // Reset form
  function resetForm() {
    setName('');
    setCategory(''); // No default selection
    setDescription('');
    setAmount('');
    setAmountType('fixed');
    setJurisdictionIds([]);
    setUtilityProvider('');
    setStartDate('');
    setEndDate('');
    setStatus('active');
    setDisplayPriority(2);
    setShowExpirationCountdown(true);
    setApplicationNotes('');
    setRequiredDocs('');
    setContactInfo('');
    setEquipmentTypes([]);
    setMinSEER('');
    setMinHSPF('');
    setMinAFUE('');
    setIsStackable(true);
    setAllowedWith([]);
    setNotAllowedWith([]);
    setApplicableSystemTypes([]);
    setApplicableSystemOptions([]);
    setEditingRebate(null);
  }

  // Close modal
  function handleCloseModal() {
    setShowModal(false);
    resetForm();
    setError(null);
  }

  // Save rebate
  async function handleSave(e) {
    e.preventDefault();
    e.stopPropagation();

    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      // Validation
      if (!name.trim()) throw new Error('Rebate name is required');
      if (!amount || parseFloat(amount) < 0) throw new Error('Valid amount is required');
      if (!startDate || !endDate) throw new Error('Start and end dates are required');
      if (new Date(endDate) < new Date(startDate)) throw new Error('End date must be after start date');

      // Build eligibility rules
      const eligibilityRules = {
        equipment_types: equipmentTypes,
        ...(minSEER && { min_seer: parseFloat(minSEER) }),
        ...(minHSPF && { min_hspf: parseFloat(minHSPF) }),
        ...(minAFUE && { min_afue: parseFloat(minAFUE) })
      };

      // Build stackability rules
      const stackabilityRules = {
        stackable: isStackable,
        allowed_with: allowedWith,
        not_allowed_with: notAllowedWith
      };

      const rebateData = {
        contractor_id: contractor.id,
        name: name.trim(),
        category,
        description: description.trim() || null,
        amount: parseFloat(amount),
        amount_type: amountType,
        jurisdiction_ids: jurisdictionIds.length > 0 ? jurisdictionIds : null,
        utility_provider: utilityProvider.trim() || null,
        eligibility_rules: eligibilityRules,
        stackability_rules: stackabilityRules,
        start_date: startDate,
        end_date: endDate,
        status,
        display_priority: displayPriority,
        show_expiration_countdown: showExpirationCountdown,
        application_notes: applicationNotes.trim() || null,
        required_docs: requiredDocs.trim() || null,
        contact_info: contactInfo.trim() || null,
        applicable_system_types: applicableSystemTypes.length > 0 ? applicableSystemTypes : null,
        applicable_system_options: applicableSystemOptions.length > 0
          ? applicableSystemOptions.map(id => String(id)) // Ensure UUIDs are strings
          : null
      };

      console.log('Saving rebate data:', rebateData);
      console.log('applicable_system_options type:', typeof applicableSystemOptions[0], applicableSystemOptions);

      if (modalMode === 'edit') {
        const { error: updateError } = await supabase
          .from('rebates')
          .update(rebateData)
          .eq('id', editingRebate.id);

        if (updateError) {
          console.error('Update error details:', updateError);
          throw updateError;
        }

        logInfo('update_rebate', `Updated rebate: ${name}`, {
          rebate_id: editingRebate.id,
          rebate_name: name,
          category,
          amount: parseFloat(amount),
          amount_type: amountType
        });
      } else {
        const { error: insertError } = await supabase
          .from('rebates')
          .insert(rebateData);

        if (insertError) {
          console.error('Insert error details:', insertError);
          throw insertError;
        }

        logInfo('create_rebate', `Created new rebate: ${name}`, {
          rebate_name: name,
          category,
          amount: parseFloat(amount),
          amount_type: amountType
        });
      }

      await loadRebates();
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      handleCloseModal();
    } catch (err) {
      console.error('Error saving rebate:', err);
      console.error('Error details:', JSON.stringify(err, null, 2));
      logError(
        modalMode === 'edit' ? 'update_rebate' : 'create_rebate',
        err,
        {
          rebate_name: name,
          category,
          amount: parseFloat(amount),
          amount_type: amountType,
          error_hint: err.hint,
          error_details: err.details,
          error_code: err.code
        }
      );
      setError(err.message || err.hint || err.details || 'Failed to save rebate');
    } finally {
      setSaving(false);
    }
  }

  // Delete rebate
  async function handleDelete(rebateId) {
    if (!confirm('Are you sure you want to delete this rebate? This cannot be undone.')) {
      return;
    }

    const rebateToDelete = rebates.find(r => r.id === rebateId);

    try {
      setSaving(true);
      setError(null);

      const { error: deleteError } = await supabase
        .from('rebates')
        .delete()
        .eq('id', rebateId);

      if (deleteError) throw deleteError;

      logInfo('delete_rebate', `Deleted rebate: ${rebateToDelete?.name || 'Unknown'}`, {
        rebate_id: rebateId,
        rebate_name: rebateToDelete?.name,
        category: rebateToDelete?.category
      });

      await loadRebates();
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error deleting rebate:', err);
      logError('delete_rebate', err, {
        rebate_id: rebateId,
        rebate_name: rebateToDelete?.name
      });
      setError(err.message || 'Failed to delete rebate');
    } finally {
      setSaving(false);
    }
  }

  // Filter rebates
  const filteredRebates = rebates.filter(rebate => {
    // Category filter
    if (filterCategory !== 'all' && rebate.category !== filterCategory) return false;

    // Status filter
    if (filterStatus !== 'all') {
      const urgency = getUrgencyInfo(rebate.end_date).urgency;
      if (filterStatus === 'active' && urgency === 'expired') return false;
      if (filterStatus === 'expired' && urgency !== 'expired') return false;
      if (filterStatus === 'expiring' && !['urgent', 'act_fast', 'expires_soon'].includes(urgency)) return false;
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();

      // Get jurisdiction names for this rebate
      const rebateJurisdictions = (rebate.jurisdiction_ids || [])
        .map(id => jurisdictions.find(j => j.id === id))
        .filter(Boolean)
        .map(j => j.jurisdiction_name.toLowerCase())
        .join(' ');

      return (
        rebate.name.toLowerCase().includes(query) ||
        rebate.description?.toLowerCase().includes(query) ||
        rebateJurisdictions.includes(query) ||
        rebate.utility_provider?.toLowerCase().includes(query)
      );
    }

    return true;
  });

  // Sort all rebates by category priority (promotions first), then by urgency
  const sortedRebates = filteredRebates.sort((a, b) => {
    // Promotions first
    if (a.category === 'contractor_promotion' && b.category !== 'contractor_promotion') return -1;
    if (b.category === 'contractor_promotion' && a.category !== 'contractor_promotion') return 1;

    // Then by display priority
    if (a.display_priority !== b.display_priority) return a.display_priority - b.display_priority;

    // Then by expiration date (soonest first)
    return new Date(a.end_date) - new Date(b.end_date);
  });

  if (loading) {
    return (
      <div className="card">
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <div style={{ fontSize: '16px', color: '#64748b' }}>Loading rebates...</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Success Message */}
      {success && (
        <div style={{
          padding: '15px',
          background: '#dcfce7',
          border: '2px solid #22c55e',
          borderRadius: '8px',
          color: '#14532d',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <span>✅</span>
          <strong>Rebate saved successfully!</strong>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div style={{
          padding: '15px',
          background: '#fee2e2',
          border: '2px solid #ef4444',
          borderRadius: '8px',
          color: '#dc2626',
          marginBottom: '20px'
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Info Card */}
      <div style={{
        padding: '15px',
        background: '#f0f9ff',
        borderRadius: '8px',
        marginBottom: '24px',
        fontSize: '14px',
        color: '#0c4a6e',
        lineHeight: '1.6'
      }}>
        <strong>💡 About Rebates & Incentives:</strong> Manage all available rebates and create your own company promotions.
        Rebates with approaching expiration dates will display urgency indicators to help close deals faster.
        You can configure which rebates can be stacked together during assessments.
      </div>

      {/* Filters */}
      <div style={{
        display: 'flex',
        gap: '15px',
        marginBottom: '20px',
        flexWrap: 'wrap'
      }}>
        <input
          type="text"
          placeholder="Search rebates..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            flex: '1 1 250px',
            padding: '10px 15px',
            border: '2px solid #e0e6ed',
            borderRadius: '8px',
            fontSize: '14px'
          }}
        />
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          style={{
            padding: '10px 15px',
            border: '2px solid #e0e6ed',
            borderRadius: '8px',
            fontSize: '14px',
            cursor: 'pointer'
          }}
        >
          <option value="all">All Categories</option>
          {categories.map(cat => (
            <option key={cat.value} value={cat.value}>{cat.label}</option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          style={{
            padding: '10px 15px',
            border: '2px solid #e0e6ed',
            borderRadius: '8px',
            fontSize: '14px',
            cursor: 'pointer'
          }}
        >
          <option value="all">All Statuses</option>
          <option value="active">Active</option>
          <option value="expiring">Expiring Soon</option>
          <option value="expired">Expired</option>
        </select>
      </div>

      {/* Rebates & Incentives Section */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '15px' }}>
          <div>
            <h2 style={{ fontSize: '20px', margin: '0 0 5px 0' }}>💰 Rebates & Incentives</h2>
            <p style={{ margin: 0, fontSize: '14px', color: '#64748b' }}>
              {sortedRebates.length} {sortedRebates.length === 1 ? 'rebate' : 'rebates'} configured
            </p>
          </div>
          <button
            onClick={handleAdd}
            className="btn btn-primary"
            disabled={saving}
          >
            + Add Rebate
          </button>
        </div>

        {sortedRebates.length === 0 ? (
          <div style={{
            padding: '60px 40px',
            textAlign: 'center',
            background: '#f8fafc',
            borderRadius: '8px',
            color: '#64748b'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '15px' }}>💰</div>
            <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px', color: '#2c3e50' }}>No rebates configured</div>
            <div style={{ fontSize: '14px', marginBottom: '20px', lineHeight: '1.6' }}>
              Add federal, state, utility, manufacturer rebates,<br />
              or create your own company promotions
            </div>
            <button
              onClick={handleAdd}
              className="btn btn-primary"
              disabled={saving}
            >
              + Add Your First Rebate
            </button>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '12px' }}>
            {sortedRebates.map(rebate => {
              const urgencyInfo = getUrgencyInfo(rebate.end_date);
              return (
                <RebateCard
                  key={rebate.id}
                  rebate={rebate}
                  urgencyInfo={urgencyInfo}
                  onEdit={() => handleEdit(rebate)}
                  onDelete={() => handleDelete(rebate.id)}
                  saving={saving}
                  categories={categories}
                  jurisdictions={jurisdictions}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && createPortal(
        <RebateModal
          mode={modalMode}
          saving={saving}
          onClose={handleCloseModal}
          onSave={handleSave}
          name={name}
          setName={setName}
          category={category}
          setCategory={setCategory}
          description={description}
          setDescription={setDescription}
          amount={amount}
          setAmount={setAmount}
          amountType={amountType}
          setAmountType={setAmountType}
          jurisdictionIds={jurisdictionIds}
          setJurisdictionIds={setJurisdictionIds}
          jurisdictions={jurisdictions}
          showJurisdictionInfo={showJurisdictionInfo}
          setShowJurisdictionInfo={setShowJurisdictionInfo}
          utilityProvider={utilityProvider}
          setUtilityProvider={setUtilityProvider}
          utilityProviders={utilityProviders}
          showUtilityProviderInfo={showUtilityProviderInfo}
          setShowUtilityProviderInfo={setShowUtilityProviderInfo}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          status={status}
          setStatus={setStatus}
          displayPriority={displayPriority}
          setDisplayPriority={setDisplayPriority}
          showExpirationCountdown={showExpirationCountdown}
          setShowExpirationCountdown={setShowExpirationCountdown}
          applicationNotes={applicationNotes}
          setApplicationNotes={setApplicationNotes}
          requiredDocs={requiredDocs}
          setRequiredDocs={setRequiredDocs}
          contactInfo={contactInfo}
          setContactInfo={setContactInfo}
          equipmentTypes={equipmentTypes}
          setEquipmentTypes={setEquipmentTypes}
          minSEER={minSEER}
          setMinSEER={setMinSEER}
          minHSPF={minHSPF}
          setMinHSPF={setMinHSPF}
          minAFUE={minAFUE}
          setMinAFUE={setMinAFUE}
          isStackable={isStackable}
          setIsStackable={setIsStackable}
          notAllowedWith={notAllowedWith}
          setNotAllowedWith={setNotAllowedWith}
          applicableSystemTypes={applicableSystemTypes}
          setApplicableSystemTypes={setApplicableSystemTypes}
          applicableSystemOptions={applicableSystemOptions}
          setApplicableSystemOptions={setApplicableSystemOptions}
          showSystemOptionsModal={showSystemOptionsModal}
          setShowSystemOptionsModal={setShowSystemOptionsModal}
          rebates={rebates}
          editingRebate={editingRebate}
          categories={categories}
          systemTypes={systemTypes}
          systemOptions={systemOptions}
        />,
        document.body
      )}
    </div>
  );
}

// Rebate Card Component
function RebateCard({ rebate, urgencyInfo, onEdit, onDelete, saving, categories, jurisdictions }) {
  const categoryInfo = categories.find(c => c.value === rebate.category) || { icon: '📋', label: rebate.category };

  // Get jurisdiction names for display
  const rebateJurisdictions = (rebate.jurisdiction_ids || [])
    .map(id => jurisdictions.find(j => j.id === id))
    .filter(Boolean);

  return (
    <div style={{
      padding: '16px',
      background: urgencyInfo.urgency === 'expired' ? '#f8fafc' : '#ffffff',
      border: `2px solid ${urgencyInfo.urgency === 'expired' ? '#cbd5e1' : '#e0e6ed'}`,
      borderRadius: '8px',
      opacity: urgencyInfo.urgency === 'expired' ? 0.6 : 1
    }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '16px', alignItems: 'start' }}>
        {/* Left side - Info */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '20px' }}>{categoryInfo.icon}</span>
            <div style={{ fontSize: '16px', fontWeight: '600', color: '#2c3e50' }}>
              {rebate.name}
            </div>
            <span style={{
              padding: '3px 8px',
              background: '#f1f5f9',
              color: '#475569',
              fontSize: '11px',
              fontWeight: '600',
              borderRadius: '4px',
              textTransform: 'uppercase'
            }}>
              {removeCategoryIcon(categoryInfo.label)}
            </span>
          </div>

          <div style={{ fontSize: '24px', fontWeight: '700', color: '#667eea', marginBottom: '8px', marginLeft: '30px' }}>
            {rebate.amount_type === 'percentage' ? `${rebate.amount}%` : `$${rebate.amount.toFixed(2)}`}
            {rebate.amount_type === 'tiered' && <span style={{ fontSize: '14px', fontWeight: '400', color: '#64748b' }}> (tiered)</span>}
          </div>

          {rebate.description && (
            <div style={{ fontSize: '14px', color: '#475569', marginBottom: '8px', marginLeft: '30px' }}>
              {rebate.description}
            </div>
          )}

          {rebate.show_expiration_countdown && (
            <div style={{
              display: 'inline-block',
              padding: '6px 12px',
              background: urgencyInfo.color + '15',
              border: `2px solid ${urgencyInfo.color}`,
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: '600',
              color: urgencyInfo.color,
              marginLeft: '30px'
            }}>
              {urgencyInfo.label}
            </div>
          )}

          {rebateJurisdictions.length > 0 && (
            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '8px', marginLeft: '30px' }}>
              📍 {rebateJurisdictions.map(j => j.jurisdiction_name).join(', ')}
            </div>
          )}
        </div>

        {/* Right side - Actions */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <button
            onClick={onEdit}
            className="btn btn-secondary"
            style={{ fontSize: '13px', padding: '6px 12px' }}
            disabled={saving}
          >
            Edit
          </button>
          <button
            onClick={onDelete}
            className="btn"
            style={{
              fontSize: '13px',
              padding: '6px 12px',
              background: '#fee2e2',
              color: '#dc2626',
              border: '2px solid #ef4444'
            }}
            disabled={saving}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

// System Options Modal Component
function SystemOptionsModal({ showSystemOptionsModal, setShowSystemOptionsModal, systemOptions, applicableSystemOptions, setApplicableSystemOptions }) {
  if (!showSystemOptionsModal) return null;

  const modalContent = (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        padding: '20px'
      }}
      onClick={() => setShowSystemOptionsModal(false)}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          padding: '30px',
          maxWidth: '800px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ fontSize: '20px', marginBottom: '20px', fontWeight: '700' }}>
          Select System Options
        </h3>

        {systemOptions.length === 0 ? (
          <div style={{
            padding: '40px 20px',
            textAlign: 'center',
            background: '#f8fafc',
            borderRadius: '8px',
            color: '#64748b',
            marginBottom: '20px'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '15px' }}>⚙️</div>
            <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px', color: '#2c3e50' }}>
              No system options configured
            </div>
            <div style={{ fontSize: '14px' }}>
              Configure your system options in Settings → Systems first
            </div>
          </div>
        ) : (
          <div style={{ marginBottom: '20px' }}>
            <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '20px', lineHeight: '1.6' }}>
              Select which specific system options this rebate applies to.
              Click on individual options to add or remove them.
            </div>

            <div style={{ display: 'grid', gap: '12px' }}>
              {systemOptions.map(opt => {
                const isSelected = applicableSystemOptions.includes(opt.id);

                return (
                  <div
                    key={opt.id}
                    onClick={() => {
                      if (isSelected) {
                        setApplicableSystemOptions(applicableSystemOptions.filter(id => id !== opt.id));
                      } else {
                        setApplicableSystemOptions([...applicableSystemOptions, opt.id]);
                      }
                    }}
                    style={{
                      padding: '16px',
                      background: isSelected ? '#fef3c7' : 'white',
                      border: `2px solid ${isSelected ? '#f59e0b' : '#e0e6ed'}`,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'start' }}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {}}
                        style={{
                          width: '20px',
                          height: '20px',
                          cursor: 'pointer',
                          marginTop: '2px'
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />

                      <div style={{ flex: 1 }}>
                        <div style={{ marginBottom: '8px' }}>
                          <div style={{ fontSize: '16px', fontWeight: '700', color: '#2c3e50', marginBottom: '4px' }}>
                            Option {opt.option_number}: {opt.option_name}
                          </div>
                          <div style={{ fontSize: '13px', color: '#64748b' }}>
                            {formatSystemType(opt.system_type)}
                          </div>
                        </div>

                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                          gap: '12px',
                          fontSize: '13px'
                        }}>
                          {(opt.seer2_rating || opt.afue_rating) && (
                            <div>
                              <div style={{ color: '#64748b', fontSize: '11px', marginBottom: '2px' }}>Efficiency</div>
                              <div style={{ fontWeight: '600', color: '#2c3e50' }}>
                                {opt.seer2_rating && `${opt.seer2_rating} SEER2`}
                                {opt.afue_rating && `${opt.afue_rating}% AFUE`}
                              </div>
                            </div>
                          )}
                          <div>
                            <div style={{ color: '#64748b', fontSize: '11px', marginBottom: '2px' }}>Starting Price</div>
                            <div style={{ fontWeight: '600', color: '#2c3e50' }}>
                              ${opt.starting_price?.toLocaleString()}
                            </div>
                          </div>
                          {opt.monthly_payment && opt.finance_months && (
                            <div>
                              <div style={{ color: '#64748b', fontSize: '11px', marginBottom: '2px' }}>Financing</div>
                              <div style={{ fontWeight: '600', color: '#2c3e50' }}>
                                ${opt.monthly_payment} per month ({opt.finance_months} months)
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <button
          onClick={() => setShowSystemOptionsModal(false)}
          className="btn btn-primary"
          style={{ width: '100%' }}
        >
          Done
        </button>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

// Modal Component
function RebateModal({
  mode, saving, onClose, onSave,
  name, setName, category, setCategory, description, setDescription,
  amount, setAmount, amountType, setAmountType,
  jurisdictionIds, setJurisdictionIds, jurisdictions,
  showJurisdictionInfo, setShowJurisdictionInfo,
  utilityProvider, setUtilityProvider,
  utilityProviders, showUtilityProviderInfo, setShowUtilityProviderInfo,
  startDate, setStartDate, endDate, setEndDate,
  status, setStatus, displayPriority, setDisplayPriority,
  showExpirationCountdown, setShowExpirationCountdown,
  applicationNotes, setApplicationNotes, requiredDocs, setRequiredDocs,
  contactInfo, setContactInfo,
  equipmentTypes, setEquipmentTypes,
  minSEER, setMinSEER, minHSPF, setMinHSPF, minAFUE, setMinAFUE,
  isStackable, setIsStackable,
  notAllowedWith, setNotAllowedWith,
  applicableSystemTypes, setApplicableSystemTypes,
  applicableSystemOptions, setApplicableSystemOptions,
  showSystemOptionsModal, setShowSystemOptionsModal,
  rebates, editingRebate,
  categories, systemTypes, systemOptions
}) {
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px',
        overflowY: 'auto'
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          padding: '30px',
          maxWidth: '700px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ fontSize: '20px', marginBottom: '20px' }}>
          {mode === 'edit' ? 'Edit Rebate' : 'Add New Rebate'}
        </h3>

        <form onSubmit={onSave}>
          {/* Basic Info */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              Rebate Name <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="text"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Duke Energy Heat Pump Rebate 2025"
              required
              autoFocus
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '15px', marginBottom: '20px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                Category <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <select
                className="input"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                required
                style={{ color: category ? '#2c3e50' : '#94a3b8' }}
              >
                <option value="" disabled>Select a category...</option>
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                Priority
              </label>
              <select
                className="input"
                value={displayPriority}
                onChange={(e) => setDisplayPriority(parseInt(e.target.value))}
                title="Controls display order in lists (high priority shown first)"
              >
                <option value="1">High (show first)</option>
                <option value="2">Normal</option>
                <option value="3">Low (show last)</option>
              </select>
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              Description
            </label>
            <textarea
              className="input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of the rebate and eligibility"
              rows={3}
              style={{ resize: 'vertical' }}
            />
          </div>

          {/* Amount */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '15px', marginBottom: '20px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                Amount <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <div style={{ position: 'relative' }}>
                {amountType !== 'tiered' && (
                  <span style={{
                    position: 'absolute',
                    left: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#64748b',
                    fontWeight: '600'
                  }}>
                    {amountType === 'percentage' ? '%' : '$'}
                  </span>
                )}
                <input
                  type="number"
                  className="input"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder={
                    amountType === 'percentage' ? '10' :
                    amountType === 'tiered' ? 'Base amount' :
                    '500.00'
                  }
                  step={amountType === 'percentage' ? '1' : '0.01'}
                  min="0"
                  max={amountType === 'percentage' ? '100' : undefined}
                  style={{ paddingLeft: amountType === 'tiered' ? '12px' : '28px' }}
                  required
                />
              </div>
              {amountType === 'percentage' && (
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                  Enter percentage (e.g., 10 for 10%)
                </div>
              )}
              {amountType === 'tiered' && (
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                  Enter base amount (configure tiers in eligibility)
                </div>
              )}
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                Type
              </label>
              <select
                className="input"
                value={amountType}
                onChange={(e) => setAmountType(e.target.value)}
              >
                <option value="fixed">Fixed $ Amount</option>
                <option value="percentage">Percentage %</option>
                <option value="tiered">Tiered (varies)</option>
              </select>
            </div>
          </div>

          {/* Dates */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                Start Date <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="date"
                className="input"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                required
                style={{
                  padding: '10px 12px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  cursor: 'pointer'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                End Date <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="date"
                className="input"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate || undefined}
                required
                style={{
                  padding: '10px 12px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  cursor: 'pointer'
                }}
              />
            </div>
          </div>

          {/* Geographic/Provider Info */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px', fontWeight: '600' }}>
                <span>Geographic Jurisdictions</span>
                <button
                  type="button"
                  onClick={() => setShowJurisdictionInfo(true)}
                  style={{
                    background: '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    width: '18px',
                    height: '18px',
                    fontSize: '12px',
                    fontWeight: '700',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0
                  }}
                  title="More info"
                >
                  ?
                </button>
              </label>

              {/* Dropdown to add jurisdictions */}
              <select
                className="input"
                value=""
                onChange={(e) => {
                  const jurisdictionId = e.target.value;
                  if (jurisdictionId && !jurisdictionIds.includes(jurisdictionId)) {
                    setJurisdictionIds([...jurisdictionIds, jurisdictionId]);
                  }
                  e.target.value = ''; // Reset dropdown
                }}
                style={{
                  color: '#2c3e50',
                  marginBottom: '12px'
                }}
              >
                <option value="">+ Select a jurisdiction...</option>
                {jurisdictions.length === 0 ? (
                  <option disabled>No jurisdictions configured - set up in Geography tab</option>
                ) : (
                  <>
                    {jurisdictions.filter(j => j.jurisdiction_type === 'state' && !jurisdictionIds.includes(j.id)).length > 0 && (
                      <optgroup label="States">
                        {jurisdictions
                          .filter(j => j.jurisdiction_type === 'state' && !jurisdictionIds.includes(j.id))
                          .map(j => (
                            <option key={j.id} value={j.id}>
                              {j.jurisdiction_name}
                            </option>
                          ))
                        }
                      </optgroup>
                    )}
                    {jurisdictions.filter(j => j.jurisdiction_type === 'county' && !jurisdictionIds.includes(j.id)).length > 0 && (
                      <optgroup label="Counties">
                        {jurisdictions
                          .filter(j => j.jurisdiction_type === 'county' && !jurisdictionIds.includes(j.id))
                          .map(j => (
                            <option key={j.id} value={j.id}>
                              {j.jurisdiction_name}
                              {j.parent_jurisdiction && ` (${j.parent_jurisdiction})`}
                            </option>
                          ))
                        }
                      </optgroup>
                    )}
                    {jurisdictions.filter(j => j.jurisdiction_type === 'city' && !jurisdictionIds.includes(j.id)).length > 0 && (
                      <optgroup label="Cities/Townships">
                        {jurisdictions
                          .filter(j => j.jurisdiction_type === 'city' && !jurisdictionIds.includes(j.id))
                          .map(j => (
                            <option key={j.id} value={j.id}>
                              {j.jurisdiction_name}
                              {j.parent_jurisdiction && ` (${j.parent_jurisdiction})`}
                            </option>
                          ))
                        }
                      </optgroup>
                    )}
                  </>
                )}
              </select>

              {/* Pills display for selected jurisdictions */}
              {jurisdictionIds.length > 0 ? (
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px',
                  padding: '12px',
                  background: '#eff6ff',
                  borderRadius: '8px',
                  border: '2px solid #3b82f6'
                }}>
                  {jurisdictionIds.map(jurisdictionId => {
                    const jurisdiction = jurisdictions.find(j => j.id === jurisdictionId);
                    if (!jurisdiction) return null;

                    // Get icon based on type
                    const icon = jurisdiction.jurisdiction_type === 'state' ? '🏛️' :
                                 jurisdiction.jurisdiction_type === 'county' ? '🏘️' : '🏙️';

                    return (
                      <div
                        key={jurisdictionId}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '6px 12px',
                          background: '#3b82f6',
                          color: 'white',
                          borderRadius: '20px',
                          fontSize: '13px',
                          fontWeight: '500'
                        }}
                      >
                        <span>{icon} {jurisdiction.jurisdiction_name}</span>
                        <button
                          type="button"
                          onClick={() => {
                            setJurisdictionIds(jurisdictionIds.filter(id => id !== jurisdictionId));
                          }}
                          style={{
                            background: 'rgba(255, 255, 255, 0.3)',
                            border: 'none',
                            borderRadius: '50%',
                            width: '18px',
                            height: '18px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            color: 'white',
                            fontSize: '14px',
                            fontWeight: '700',
                            padding: 0
                          }}
                          title="Remove"
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{
                  padding: '12px',
                  background: '#f0fdf4',
                  borderRadius: '6px',
                  fontSize: '13px',
                  color: '#14532d',
                  border: '1px solid #86efac'
                }}>
                  ✅ Applies to all service areas
                </div>
              )}

              <div style={{ fontSize: '12px', color: '#64748b', marginTop: '8px' }}>
                Leave empty if rebate applies to all jurisdictions in your service area
              </div>
            </div>

            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px', fontWeight: '600' }}>
                <span>Utility Provider</span>
                <button
                  type="button"
                  onClick={() => setShowUtilityProviderInfo(true)}
                  style={{
                    background: '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    width: '18px',
                    height: '18px',
                    fontSize: '12px',
                    fontWeight: '700',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0
                  }}
                  title="More info"
                >
                  ?
                </button>
              </label>
              <select
                className="input"
                value={utilityProvider}
                onChange={(e) => setUtilityProvider(e.target.value)}
                style={{ color: utilityProvider ? '#2c3e50' : '#94a3b8' }}
              >
                <option value="">Not utility-specific</option>
                {utilityProviders.map(provider => (
                  <option key={provider.id} value={provider.provider_name}>
                    {provider.provider_name} ({provider.utility_type === 'electric' ? '⚡ Electric' : '🔥 Gas'})
                  </option>
                ))}
              </select>
              <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                Leave as "Not utility-specific" for most rebates
              </div>
            </div>
          </div>

          {/* Checkboxes */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              cursor: 'pointer',
              padding: '12px',
              background: '#f8fafc',
              borderRadius: '8px'
            }}>
              <input
                type="checkbox"
                checked={showExpirationCountdown}
                onChange={(e) => setShowExpirationCountdown(e.target.checked)}
              />
              <span style={{ fontWeight: '600' }}>Show expiration countdown</span>
            </label>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              cursor: 'pointer',
              padding: '12px',
              background: '#f8fafc',
              borderRadius: '8px'
            }}>
              <input
                type="checkbox"
                checked={isStackable}
                onChange={(e) => setIsStackable(e.target.checked)}
              />
              <span style={{ fontWeight: '600' }}>Can be stacked with other rebates</span>
            </label>
          </div>

          {/* Cannot Be Combined With (Mutually Exclusive Rebates) */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              Cannot Be Combined With (Mutually Exclusive)
            </label>

            {/* Dropdown to add rebates */}
            <select
              className="input"
              value=""
              onChange={(e) => {
                const rebateId = e.target.value;
                if (rebateId && !notAllowedWith.includes(rebateId)) {
                  setNotAllowedWith([...notAllowedWith, rebateId]);
                }
                e.target.value = ''; // Reset dropdown
              }}
              style={{
                color: '#2c3e50',
                marginBottom: '12px'
              }}
            >
              <option value="">+ Select a rebate to exclude...</option>
              {rebates
                .filter(r =>
                  (!editingRebate || r.id !== editingRebate.id) && // Don't show self
                  !notAllowedWith.includes(r.id) // Don't show already selected
                )
                .map(r => (
                  <option key={r.id} value={r.id}>
                    {r.name} ({r.category})
                  </option>
                ))
              }
            </select>

            {/* Pills display for selected rebates */}
            {notAllowedWith.length > 0 ? (
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                padding: '12px',
                background: '#fee2e2',
                borderRadius: '8px',
                border: '2px solid #ef4444'
              }}>
                {notAllowedWith.map(rebateId => {
                  const rebate = rebates.find(r => r.id === rebateId);
                  if (!rebate) return null;

                  return (
                    <div
                      key={rebateId}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '6px 12px',
                        background: '#dc2626',
                        color: 'white',
                        borderRadius: '20px',
                        fontSize: '13px',
                        fontWeight: '500'
                      }}
                    >
                      <span>🚫 {rebate.name}</span>
                      <button
                        type="button"
                        onClick={() => {
                          setNotAllowedWith(notAllowedWith.filter(id => id !== rebateId));
                        }}
                        style={{
                          background: 'rgba(255, 255, 255, 0.3)',
                          border: 'none',
                          borderRadius: '50%',
                          width: '18px',
                          height: '18px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          color: 'white',
                          fontSize: '14px',
                          fontWeight: '700',
                          padding: 0
                        }}
                        title="Remove"
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{
                padding: '12px',
                background: '#f0fdf4',
                borderRadius: '6px',
                fontSize: '13px',
                color: '#14532d',
                border: '1px solid #86efac'
              }}>
                ✅ This rebate can be combined with all other rebates (if stackable is enabled)
              </div>
            )}

            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '8px', lineHeight: '1.5' }}>
              <strong>💡 Tip:</strong> Select rebates that <strong>cannot</strong> be used together with this one.
              For example, if Duke Energy has a $1,000 standard heat pump rebate and a $2,000 high-efficiency
              heat pump rebate, these two should be mutually exclusive (customer can only choose one).
            </div>
          </div>

          {/* Applicable System Types */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              Applicable System Types (Optional)
            </label>

            {/* Dropdown to add system types */}
            <select
              className="input"
              value=""
              onChange={(e) => {
                const systemTypeId = e.target.value;
                if (systemTypeId && !applicableSystemTypes.includes(systemTypeId)) {
                  setApplicableSystemTypes([...applicableSystemTypes, systemTypeId]);
                }
                e.target.value = ''; // Reset dropdown
              }}
              style={{
                color: '#2c3e50',
                marginBottom: '12px'
              }}
            >
              <option value="">+ Select a system type...</option>
              {systemTypes
                .filter(st => !applicableSystemTypes.includes(st.id))
                .map(st => (
                  <option key={st.id} value={st.id}>
                    {st.icon} {st.label}
                  </option>
                ))
              }
            </select>

            {/* Pills display for selected system types */}
            {applicableSystemTypes.length > 0 ? (
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                padding: '12px',
                background: '#eff6ff',
                borderRadius: '8px',
                border: '2px solid #3b82f6'
              }}>
                {applicableSystemTypes.map(systemTypeId => {
                  const systemType = systemTypes.find(st => st.id === systemTypeId);
                  if (!systemType) return null;

                  return (
                    <div
                      key={systemTypeId}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '6px 12px',
                        background: '#3b82f6',
                        color: 'white',
                        borderRadius: '20px',
                        fontSize: '13px',
                        fontWeight: '500'
                      }}
                    >
                      <span>{systemType.icon} {systemType.label}</span>
                      <button
                        type="button"
                        onClick={() => {
                          setApplicableSystemTypes(applicableSystemTypes.filter(id => id !== systemTypeId));
                        }}
                        style={{
                          background: 'rgba(255, 255, 255, 0.3)',
                          border: 'none',
                          borderRadius: '50%',
                          width: '18px',
                          height: '18px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          color: 'white',
                          fontSize: '14px',
                          fontWeight: '700',
                          padding: 0
                        }}
                        title="Remove"
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{
                padding: '12px',
                background: '#f0fdf4',
                borderRadius: '6px',
                fontSize: '13px',
                color: '#14532d',
                border: '1px solid #86efac'
              }}>
                ✅ Applies to all system types
              </div>
            )}

            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '8px', lineHeight: '1.5' }}>
              <strong>💡 Tip:</strong> Leave empty if this rebate applies to all system types. Select specific types if the rebate is limited (e.g., "Heat Pump Rebate" would only apply to heat pump systems).
            </div>
          </div>

          {/* Applicable System Options */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              Applicable System Options (Optional)
            </label>

            {/* Button to open modal */}
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowSystemOptionsModal(true)}
              style={{
                width: '100%',
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <span>+</span>
              <span>Select System Options</span>
            </button>

            {/* Pills display for selected system options */}
            {applicableSystemOptions.length > 0 ? (
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                padding: '12px',
                background: '#fef3c7',
                borderRadius: '8px',
                border: '2px solid #f59e0b'
              }}>
                {applicableSystemOptions.map(optionId => {
                  // Find the system option by ID
                  const option = systemOptions.find(opt => opt.id === optionId);
                  if (!option) return null;

                  const displayLabel = `${option.option_name} (${formatSystemType(option.system_type)})`;

                  return (
                    <div
                      key={optionId}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '6px 12px',
                        background: '#f59e0b',
                        color: 'white',
                        borderRadius: '20px',
                        fontSize: '13px',
                        fontWeight: '500'
                      }}
                    >
                      <span>{displayLabel}</span>
                      <button
                        type="button"
                        onClick={() => {
                          setApplicableSystemOptions(applicableSystemOptions.filter(id => id !== optionId));
                        }}
                        style={{
                          background: 'rgba(255, 255, 255, 0.3)',
                          border: 'none',
                          borderRadius: '50%',
                          width: '18px',
                          height: '18px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          color: 'white',
                          fontSize: '14px',
                          fontWeight: '700',
                          padding: 0
                        }}
                        title="Remove"
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{
                padding: '12px',
                background: '#f0fdf4',
                borderRadius: '6px',
                fontSize: '13px',
                color: '#14532d',
                border: '1px solid #86efac'
              }}>
                ✅ Applies to all system options
              </div>
            )}

            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '8px', lineHeight: '1.5' }}>
              <strong>💡 Tip:</strong> Leave empty if this rebate applies to all system options (1, 2, 3). Select specific options if the rebate only applies to certain efficiency levels or price tiers configured in Settings → Systems.
            </div>
          </div>

          {/* Additional Info */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              Application Notes
            </label>
            <textarea
              className="input"
              value={applicationNotes}
              onChange={(e) => setApplicationNotes(e.target.value)}
              placeholder="How to apply, process details..."
              rows={2}
              style={{ resize: 'vertical' }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              Required Documentation
            </label>
            <textarea
              className="input"
              value={requiredDocs}
              onChange={(e) => setRequiredDocs(e.target.value)}
              placeholder="List of required documents..."
              rows={2}
              style={{ resize: 'vertical' }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              Contact Information
            </label>
            <input
              type="text"
              className="input"
              value={contactInfo}
              onChange={(e) => setContactInfo(e.target.value)}
              placeholder="Website, phone number, email..."
            />
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: '10px', marginTop: '24px' }}>
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
              style={{ flex: 1 }}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              style={{ flex: 1 }}
              disabled={saving}
            >
              {saving ? 'Saving...' : (mode === 'edit' ? 'Update Rebate' : 'Add Rebate')}
            </button>
          </div>
        </form>
      </div>

      {/* Utility Provider Info Modal */}
      {showUtilityProviderInfo && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '20px'
          }}
          onClick={() => setShowUtilityProviderInfo(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '500px',
              width: '100%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <div style={{
                background: '#667eea',
                color: 'white',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '18px',
                fontWeight: '700'
              }}>
                ?
              </div>
              <h3 style={{ fontSize: '20px', margin: 0, fontWeight: '700' }}>
                About Utility Provider Field
              </h3>
            </div>

            <div style={{ fontSize: '15px', color: '#475569', lineHeight: '1.7', marginBottom: '20px' }}>
              <p style={{ marginBottom: '15px' }}>
                <strong>Most rebates should leave this set to "Not utility-specific".</strong>
              </p>

              <p style={{ marginBottom: '15px' }}>
                Only select a specific utility provider if the rebate is <strong>exclusively offered by that utility company</strong>.
              </p>

              <div style={{ background: '#f0f9ff', padding: '15px', borderRadius: '8px', borderLeft: '4px solid #667eea', marginBottom: '15px' }}>
                <div style={{ fontWeight: '600', marginBottom: '8px', color: '#1e40af' }}>✅ Examples - Select Utility Provider:</div>
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  <li>Duke Energy Smart $aver® Program</li>
                  <li>PG&E Heat Pump Incentive</li>
                  <li>ConEd Efficiency Rebate</li>
                </ul>
              </div>

              <div style={{ background: '#fef3c7', padding: '15px', borderRadius: '8px', borderLeft: '4px solid #f59e0b' }}>
                <div style={{ fontWeight: '600', marginBottom: '8px', color: '#92400e' }}>❌ Examples - Leave as "Not utility-specific":</div>
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  <li>Federal Tax Credits (IRS)</li>
                  <li>State Rebates</li>
                  <li>Manufacturer Rebates</li>
                  <li>Your Company Promotions</li>
                </ul>
              </div>
            </div>

            <button
              onClick={() => setShowUtilityProviderInfo(false)}
              className="btn btn-primary"
              style={{ width: '100%' }}
            >
              Got it!
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* Geographic Jurisdiction Info Modal */}
      {showJurisdictionInfo && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '20px'
          }}
          onClick={() => setShowJurisdictionInfo(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '500px',
              width: '100%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <div style={{
                background: '#667eea',
                color: 'white',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '18px',
                fontWeight: '700'
              }}>
                ?
              </div>
              <h3 style={{ fontSize: '20px', margin: 0, fontWeight: '700' }}>
                About Geographic Jurisdictions
              </h3>
            </div>

            <div style={{ fontSize: '15px', color: '#475569', lineHeight: '1.7', marginBottom: '20px' }}>
              <p style={{ marginBottom: '15px' }}>
                <strong>Most rebates should leave this empty (applies to all areas you serve).</strong>
              </p>

              <p style={{ marginBottom: '15px' }}>
                Only select specific jurisdictions if the rebate is <strong>limited to certain geographic areas</strong>.
                You can select multiple jurisdictions if a rebate applies in several areas.
              </p>

              <div style={{ background: '#f0f9ff', padding: '15px', borderRadius: '8px', borderLeft: '4px solid #667eea', marginBottom: '15px' }}>
                <div style={{ fontWeight: '600', marginBottom: '8px', color: '#1e40af' }}>✅ Examples - Select Jurisdiction(s):</div>
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  <li>City of Greensboro Heat Pump Program</li>
                  <li>Guilford County Energy Efficiency Grant</li>
                  <li>North Carolina State Rebate (select "North Carolina")</li>
                </ul>
              </div>

              <div style={{ background: '#fef3c7', padding: '15px', borderRadius: '8px', borderLeft: '4px solid #f59e0b' }}>
                <div style={{ fontWeight: '600', marginBottom: '8px', color: '#92400e' }}>❌ Examples - Leave empty:</div>
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  <li>Federal Tax Credits (IRS)</li>
                  <li>Utility company rebates (available across all their service areas)</li>
                  <li>Manufacturer Rebates</li>
                  <li>Your Company Promotions</li>
                </ul>
              </div>

              <div style={{ background: '#f0fdf4', padding: '12px', borderRadius: '6px', fontSize: '13px', color: '#14532d', marginTop: '15px' }}>
                <strong>💡 Tip:</strong> Set up your jurisdictions in the Geography tab first. During assessments,
                you'll be asked which area the customer is in, and only matching rebates will appear.
              </div>
            </div>

            <button
              onClick={() => setShowJurisdictionInfo(false)}
              className="btn btn-primary"
              style={{ width: '100%' }}
            >
              Got it!
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* System Options Selection Modal */}
      <SystemOptionsModal
        showSystemOptionsModal={showSystemOptionsModal}
        setShowSystemOptionsModal={setShowSystemOptionsModal}
        systemOptions={systemOptions}
        applicableSystemOptions={applicableSystemOptions}
        setApplicableSystemOptions={setApplicableSystemOptions}
      />
    </div>
  );
}
